// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const CANONICAL_FIELDS = [
    // ── Organization ──────────────────────────────────────────
    {
        key: 'organization_name',
        label: { en: 'Organization Name', vi: 'T\u00ean t\u1ed5 ch\u1ee9c' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification', 'authority_request', 'dossier_filing'],
        template_field_ids: ['org_name', 'entity_name', 'controller_name', 'transferor_name', 'vendor_name'],
        source: 'human_entered',
        validation: { required: true, max_length: 200 },
    },
    {
        key: 'organization_address',
        label: { en: 'Organization Address', vi: '\u0110\u1ecba ch\u1ec9 t\u1ed5 ch\u1ee9c' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification', 'dossier_filing'],
        template_field_ids: ['org_address', 'entity_address', 'controller_address', 'transferor_address'],
        source: 'human_entered',
    },
    // ── Contact / DPO ─────────────────────────────────────────
    {
        key: 'dpo_name',
        label: { en: 'DPO Name', vi: 'T\u00ean DPO' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification'],
        template_field_ids: ['dpo_name', 'dpo_contact_name', 'data_protection_officer'],
        source: 'human_entered',
    },
    {
        key: 'dpo_email',
        label: { en: 'DPO Email', vi: 'Email DPO' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification'],
        template_field_ids: ['dpo_email', 'dpo_contact_email'],
        source: 'human_entered',
        validation: { pattern: '^[^@]+@[^@]+\\.[^@]+$' },
    },
    {
        key: 'contact_person',
        label: { en: 'Contact Person', vi: 'Ng\u01b0\u1eddi li\u00ean h\u1ec7' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification', 'authority_request', 'dossier_filing'],
        template_field_ids: ['contact_person', 'contact_name', 'applicant_contact'],
        source: 'human_entered',
    },
    // ── Processing ────────────────────────────────────────────
    {
        key: 'processing_purpose',
        label: { en: 'Processing Purpose', vi: 'M\u1ee5c \u0111\u00edch x\u1eed l\u00fd' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer', 'breach_notification'],
        template_field_ids: ['processing_purpose', 'purpose', 'purpose_of_processing'],
        source: 'human_entered',
        validation: { required: true },
    },
    {
        key: 'processing_description',
        label: { en: 'Processing Description', vi: 'M\u00f4 t\u1ea3 x\u1eed l\u00fd' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer'],
        template_field_ids: ['processing_description', 'description', 'processing_details'],
        source: 'human_entered',
    },
    {
        key: 'legal_basis',
        label: { en: 'Legal Basis', vi: 'C\u01a1 s\u1edf ph\u00e1p l\u00fd' },
        type: 'string',
        cardinality: 'single',
        modules: ['dpia', 'transfer'],
        template_field_ids: ['legal_basis', 'lawful_basis', 'processing_legal_basis'],
        source: 'human_entered',
    },
    // ── Data ──────────────────────────────────────────────────
    {
        key: 'data_categories',
        label: { en: 'Data Categories', vi: 'Lo\u1ea1i d\u1eef li\u1ec7u' },
        type: 'string[]',
        cardinality: 'array',
        modules: ['dpia', 'transfer', 'breach_notification', 'incident'],
        template_field_ids: ['data_types', 'data_categories', 'data_categories_affected', 'data_collected', 'data_elements'],
        source: 'human_entered',
    },
    {
        key: 'data_subjects',
        label: { en: 'Data Subjects', vi: 'Ch\u1ee7 th\u1ec3 d\u1eef li\u1ec7u' },
        type: 'string[]',
        cardinality: 'array',
        modules: ['dpia', 'transfer', 'breach_notification'],
        template_field_ids: ['data_subjects', 'affected_data_subjects', 'subject_categories'],
        source: 'human_entered',
    },
    {
        key: 'records_affected',
        label: { en: 'Records Affected', vi: 'S\u1ed1 b\u1ea3n ghi b\u1ecb \u1ea3nh h\u01b0\u1edfng' },
        type: 'number',
        cardinality: 'single',
        modules: ['breach_notification', 'incident'],
        template_field_ids: ['records_affected', 'estimated_records', 'number_of_records'],
        source: 'human_entered',
    },
    // ── Transfer-specific ─────────────────────────────────────
    {
        key: 'destination_country',
        label: { en: 'Destination Country', vi: 'Qu\u1ed1c gia \u0111\u00edch' },
        type: 'string',
        cardinality: 'single',
        modules: ['transfer'],
        template_field_ids: ['destination_country', 'recipient_country', 'transfer_destination'],
        source: 'human_entered',
    },
    {
        key: 'recipient_name',
        label: { en: 'Recipient Name', vi: 'T\u00ean b\u00ean nh\u1eadn' },
        type: 'string',
        cardinality: 'single',
        modules: ['transfer'],
        template_field_ids: ['recipient_name', 'transferee_name', 'data_importer'],
        source: 'human_entered',
    },
    {
        key: 'transfer_mechanism',
        label: { en: 'Transfer Mechanism', vi: 'C\u01a1 ch\u1ebf chuy\u1ec3n d\u1eef li\u1ec7u' },
        type: 'string',
        cardinality: 'single',
        modules: ['transfer'],
        template_field_ids: ['transfer_mechanism', 'legal_mechanism', 'safeguard_mechanism'],
        source: 'human_entered',
    },
    // ── Linked entities ───────────────────────────────────────
    {
        key: 'systems',
        label: { en: 'Systems', vi: 'H\u1ec7 th\u1ed1ng' },
        type: 'object[]',
        cardinality: 'nested_array',
        modules: ['dpia', 'transfer'],
        template_field_ids: ['system_ids', 'systems_affected'],
        source: 'linked_entity',
    },
    {
        key: 'risk_items',
        label: { en: 'Risk Items', vi: 'M\u1ee5c r\u1ee7i ro' },
        type: 'object[]',
        cardinality: 'nested_array',
        modules: ['dpia'],
        template_field_ids: ['identified_risks', 'risk_items'],
        source: 'human_entered',
    },
    // ── Incident / Breach ─────────────────────────────────────
    {
        key: 'incident_description',
        label: { en: 'Incident Description', vi: 'M\u00f4 t\u1ea3 s\u1ef1 c\u1ed1' },
        type: 'string',
        cardinality: 'single',
        modules: ['breach_notification', 'incident'],
        template_field_ids: ['incident_description', 'breach_description', 'event_description'],
        source: 'human_entered',
    },
    {
        key: 'discovery_date',
        label: { en: 'Discovery Date', vi: 'Ng\u00e0y ph\u00e1t hi\u1ec7n' },
        type: 'date',
        cardinality: 'single',
        modules: ['breach_notification', 'incident'],
        template_field_ids: ['discovery_date', 'date_discovered', 'breach_discovery_date'],
        source: 'human_entered',
    },
    {
        key: 'measures_taken',
        label: { en: 'Measures Taken', vi: 'Bi\u1ec7n ph\u00e1p \u0111\u00e3 th\u1ef1c hi\u1ec7n' },
        type: 'string',
        cardinality: 'single',
        modules: ['breach_notification', 'incident'],
        template_field_ids: ['measures_taken', 'remedial_measures', 'corrective_actions'],
        source: 'human_entered',
    },
];
/** Version hash for the canonical registry — increment on structural changes */
export const CANONICAL_REGISTRY_VERSION = '37.1.0-001';
//# sourceMappingURL=canonical-fields.js.map